import { INSTRUMENT_DEFINITIONS, } from "./questionnaires.js";
function matchesShowIf(showIf, value) {
    if (showIf.equals !== undefined) {
        return value !== undefined && value.value === showIf.equals;
    }
    if (showIf.notEquals !== undefined) {
        return value === undefined || value.value !== showIf.notEquals;
    }
    // includesAny: the gating question is multi_choice, so its value is a string[].
    const selected = value && value.kind === "multi_choice" ? value.value : [];
    return showIf.includesAny.some((id) => selected.includes(id));
}
const questionsByKeyCache = new Map();
function questionsByKey(instrument) {
    let byKey = questionsByKeyCache.get(instrument);
    if (!byKey) {
        byKey = new Map(INSTRUMENT_DEFINITIONS[instrument].questions.map((q) => [q.key, q]));
        questionsByKeyCache.set(instrument, byKey);
    }
    return byKey;
}
/** Whether a top-level question is currently shown, given the answers so
    far. True when the question has no showIf at all. Transitive: a question
    whose gating question is itself hidden is hidden too, so a stale answer
    left on a now-hidden follow-up (e.g. `contraceptives_details` after
    `contraceptives` flips to false) cannot reveal that follow-up's own
    follow-ups. */
export function isReachable(instrument, q, answers) {
    if (!q.showIf)
        return true;
    if (!matchesShowIf(q.showIf, answers.get(q.showIf.key)))
        return false;
    const gate = questionsByKey(instrument).get(q.showIf.key);
    return !gate || isReachable(instrument, gate, answers);
}
/** Whether a question is presented at all: `isReachable`, except that a
    retired question is shown only where an answer exists, so it is never
    presented as unanswered. `isReachable` itself ignores `retired`, so a
    retired question still acts as a gate for the questions behind it. */
export function isShown(instrument, q, answers, items) {
    if (q.retired)
        return isAnswered(q, answers, items);
    return isReachable(instrument, q, answers);
}
const NO_ITEMS = new Map();
/** Every top-level question of an instrument that is currently shown, in
    definition order. */
export function reachableQuestions(instrument, answers) {
    // No items argument: a retired question is always single, so `answers`
    // alone decides whether it is shown.
    return INSTRUMENT_DEFINITIONS[instrument].questions.filter((q) => isShown(instrument, q, answers, NO_ITEMS));
}
/** A repeating group's fields for one item, filtered to the ones currently
    shown. An item-scoped showIf resolves against that item's own fields, not
    the submission's top-level answers -- a global lookup would silently do
    the wrong thing once two items hold different values for the same field
    key. */
export function reachableGroupFields(group, item) {
    return group.fields.filter((field) => !field.showIf || matchesShowIf(field.showIf, item.get(field.showIf.key)));
}
/** `reachableGroupFields` minus any field written by another field's
    control (`writtenByControl`, e.g. `product_id`, set by the supplement
    picker alongside `name`) rather than rendered and answered on its own.
    Nothing today derives a completeness count from a group's fields --
    isAnswered treats a repeating question as answered once it has any item
    at all -- but if a future check ever counts answered/unanswered fields
    within an item, it must start from this list, not reachableGroupFields
    directly, or a control-written field would misread as an unanswered
    question the client never saw. */
export function answerableGroupFields(group, item) {
    return reachableGroupFields(group, item).filter((field) => !field.writtenByControl);
}
/** A single question counts as answered by an entry in `answers`; a
    repeating-group question counts as answered once it has at least one
    item. */
export function isAnswered(q, answers, items) {
    if (q.repeating)
        return (items.get(q.key)?.length ?? 0) > 0;
    return answers.has(q.key);
}
/** Whether a question counts toward a section's completion denominator.
    `kind === "text"` is excluded -- questionnaires.ts's own comment on
    `FREE_TEXT_MAX` already states the design: "every free-text question in
    v1 is optional and never blocks a section". A free-text field is always
    an elaboration on a choice made elsewhere (`medical_conditions_other`
    next to the `medical_conditions_details` picklist, the whole of
    `additional_information`'s one question) -- never the only way to answer
    something, so leaving it blank never reads as unfinished. */
function countsTowardCompletion(q) {
    return q.repeating || q.kind !== "text";
}
/** Per-section answered/reachable counts -- the hub's completion checkmark
    and the moving denominator behind it. Counts only currently-reachable,
    completion-counting questions: a section's denominator growing when a
    follow-up is revealed is a local, visible consequence of the answer just
    given, not a global count going backwards. A section made up entirely of
    optional free text (e.g. `additional_information`) has a denominator of
    zero, which reads as already complete -- see `sectionProgress`'s callers. */
export function sectionProgress(instrument, answers, items) {
    const byKey = questionsByKey(instrument);
    return INSTRUMENT_DEFINITIONS[instrument].sections.map((section) => {
        let answered = 0;
        let reachable = 0;
        for (const key of section.questionKeys) {
            const question = byKey.get(key);
            if (!question || !isShown(instrument, question, answers, items) || !countsTowardCompletion(question))
                continue;
            reachable += 1;
            if (isAnswered(question, answers, items))
                answered += 1;
        }
        return { sectionId: section.id, answered, reachable };
    });
}
/** The keys of a section's reachable, completion-counting questions that
    have no answer yet, in definition order -- what keeps that section from
    reading as complete, so the client can be shown exactly which ones. */
export function unansweredInSection(instrument, sectionId, answers, items) {
    const byKey = questionsByKey(instrument);
    const section = INSTRUMENT_DEFINITIONS[instrument].sections.find((s) => s.id === sectionId);
    return (section?.questionKeys ?? []).filter((key) => {
        const question = byKey.get(key);
        return (question !== undefined &&
            isShown(instrument, question, answers, items) &&
            countsTowardCompletion(question) &&
            !isAnswered(question, answers, items));
    });
}
/** The hub's primary-button target: the first section (in definition order)
    with a reachable question left unanswered, or null once every reachable
    question across every section has one. */
export function firstUnfinishedSection(instrument, answers, items) {
    const unfinished = sectionProgress(instrument, answers, items).find((section) => section.answered < section.reachable);
    return unfinished?.sectionId ?? null;
}
/** The review screen's one figure: how many currently-reachable questions
    across the whole instrument have no answer yet. */
export function unansweredReachableCount(instrument, answers, items) {
    return reachableQuestions(instrument, answers).filter((q) => !isAnswered(q, answers, items)).length;
}
export function optionsForMarket(options, market) {
    return options.filter((option) => option.markets === undefined || option.markets.includes(market));
}
